import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { CircularLoading } from "@/components/common";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import type {
  BillingSendMode,
  ConsultationSituationKey,
  ConsultationSituationSegment,
  PaymentDetailData,
} from "../types/consultationsBilling.types";
import { useConsultationsBillingData } from "../hooks/useConsultationsBillingData";
import { useSendBillingCharge } from "../hooks/useSendBillingCharge";
import {
  buildConsultationSituationSegments,
  pickDefaultSituation,
} from "../utils/consultationSituationKpis";
import { sumPaymentDetailsAmount } from "../utils/getChargeAmount";
import { SituationDetailCard } from "./SituationDetailCard";
import { ConsultationsActionTable } from "./ConsultationsActionTable";
import { ConfirmBillingSendDialog } from "./ConfirmBillingSendDialog";

interface ConsultationsBillingSectionProps {
  dateRange?: {
    startDate?: string;
    endDate?: string;
  };
}

function formatCurrency(value: number) {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function KpiCard({
  segment,
  isSelected,
  onClick,
}: {
  segment: ConsultationSituationSegment;
  isSelected: boolean;
  onClick?: () => void;
}) {
  const clickable = segment.value > 0 && !!onClick;

  return (
    <Stack
      spacing={0.75}
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      sx={{
        px: 1.5,
        py: 1.5,
        minHeight: 108,
        borderRadius: 2,
        cursor: clickable ? "pointer" : "default",
        opacity: segment.value > 0 ? 1 : 0.55,
        border: "1px solid",
        borderColor: isSelected ? segment.color : "divider",
        borderLeft: "4px solid",
        borderLeftColor: isSelected ? segment.color : `${segment.color}55`,
        bgcolor: isSelected ? `${segment.color}12` : "background.paper",
        boxShadow: isSelected ? `0 0 0 1px ${segment.color}25` : "none",
        transition:
          "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease",
        ...(clickable && {
          "&:hover": {
            bgcolor: isSelected ? `${segment.color}16` : `${segment.color}08`,
            borderColor: segment.color,
          },
        }),
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.95rem", fontWeight: isSelected ? 700 : 500, lineHeight: 1.3 }}
          >
            {segment.label}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: "block", mt: 0.35, lineHeight: 1.4, fontSize: "0.875rem" }}
          >
            {segment.description}
          </Typography>
        </Box>
        {clickable && (
          <ChevronRight
            sx={{ fontSize: 20, color: segment.color, opacity: 0.7, flexShrink: 0 }}
          />
        )}
      </Stack>

      <Typography
        sx={{
          fontSize: isSelected ? "2.25rem" : "2rem",
          fontWeight: 800,
          lineHeight: 1,
          color: segment.value > 0 ? segment.color : "text.disabled",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {segment.value}
      </Typography>

      {segment.subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.875rem", lineHeight: 1.35 }}
        >
          {segment.subtitle}
        </Typography>
      )}
    </Stack>
  );
}

export function ConsultationsBillingSection({
  dateRange,
}: ConsultationsBillingSectionProps) {
  const theme = useTheme();
  const { hasFluxPayExtension } = useFluxPayContractContext();

  const [selectedSituation, setSelectedSituation] =
    useState<ConsultationSituationKey | null>(null);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchSendMode, setBatchSendMode] = useState<BillingSendMode>("send-all");
  const [pendingBatchPayments, setPendingBatchPayments] = useState<PaymentDetailData[]>(
    []
  );
  const [pendingUnsentBatchPayments, setPendingUnsentBatchPayments] = useState<
    PaymentDetailData[]
  >([]);

  const billingData = useConsultationsBillingData(dateRange);

  const { loading, error, setError, isChargeSent, sendCharges } = useSendBillingCharge({
    linkedPendingAppointments: billingData.linkedPendingAppointments,
    onSuccess: billingData.refetchPayments,
  });

  const palette = useMemo(
    () => ({
      colors: {
        booked: theme.palette.success.main,
        fulfilled: theme.palette.secondary.main,
        overdueBooked: theme.palette.deepOrange?.main ?? "#ff5722",
        closed: theme.palette.error.main,
      },
      labels: {
        booked: "Próximas consultas",
        fulfilled: "Consultas realizadas",
        overdueBooked: "Consultas atrasadas",
        closed: "Canceladas ou não realizadas",
      },
      descriptions: {
        booked: "Reagende, cobre ou mande mensagem",
        fulfilled: "Mande mensagem após a consulta",
        overdueBooked: "Paciente não veio — reagende ou mande mensagem",
        closed: "Traga o paciente de volta com mensagem",
      },
      status: {
        booked: ["marcadas"],
        fulfilled: ["fulfilled"],
        overdueBooked: ["overdueBooked"],
        closed: ["closed"],
      },
    }),
    [theme]
  );

  const segments = useMemo(
    () =>
      buildConsultationSituationSegments(
        billingData.metrics,
        palette,
        formatCurrency
      ),
    [billingData.metrics, palette]
  );

  const selectedSegment = segments.find(
    (segment) => segment.key === selectedSituation
  );

  useEffect(() => {
    if (billingData.metrics.total === 0) {
      setSelectedSituation(null);
      return;
    }
    setSelectedSituation((current) => {
      if (current && billingData.metrics[current] > 0) return current;
      return pickDefaultSituation(billingData.metrics);
    });
  }, [billingData.metrics]);

  const openBatchSendDialog = useCallback(
    (payments: PaymentDetailData[]) => {
      const unsent = payments.filter((payment) => !isChargeSent(payment));
      if (unsent.length === payments.length) setBatchSendMode("send-all");
      else if (unsent.length === 0) setBatchSendMode("resend-all");
      else setBatchSendMode("partial");
      setPendingBatchPayments(payments);
      setPendingUnsentBatchPayments(unsent);
      setBatchDialogOpen(true);
      setError(undefined);
    },
    [isChargeSent, setError]
  );

  const closeBatchDialog = () => {
    setBatchDialogOpen(false);
    setPendingBatchPayments([]);
    setPendingUnsentBatchPayments([]);
    setError(undefined);
  };

  const preloadedStats = useMemo(() => {
    if (!selectedSituation) return undefined;

    if (selectedSituation === "booked") {
      return {
        appointments: [
          ...(billingData.bookedListData?.appointments ?? []),
          ...(billingData.pendingListData?.appointments ?? []),
        ],
        patients: [
          ...(billingData.bookedListData?.patients ?? []),
          ...(billingData.pendingListData?.patients ?? []),
        ],
      };
    }

    if (selectedSituation === "overdueBooked") {
      return {
        appointments: billingData.bookedListData?.appointments ?? [],
        patients: billingData.bookedListData?.patients ?? [],
      };
    }

    return undefined;
  }, [selectedSituation, billingData.bookedListData, billingData.pendingListData]);

  if (billingData.loading) {
    return (
      <Box sx={{ py: 4 }}>
        <CircularLoading />
      </Box>
    );
  }

  if (billingData.metrics.total === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <QueryStatsIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1.5 }} />
        <Typography variant="body1" color="text.secondary">
          Nenhuma consulta ou cobrança neste período
        </Typography>
      </Box>
    );
  }

  const detailContent =
    selectedSituation && selectedSegment && selectedSegment.value > 0 ? (
      <SituationDetailCard
        color={selectedSegment.color}
        label={selectedSegment.label}
        situationKey={selectedSituation}
        meta={`${selectedSegment.value} ${
          selectedSegment.value === 1 ? "consulta" : "consultas"
        }`}
      >
        <ConsultationsActionTable
          status={
            selectedSituation === "closed" ? ["closed"] : selectedSegment.status
          }
          dateRange={dateRange}
          actors={billingData.actors}
          preloadedStats={preloadedStats}
          showAllActionIcons
          chargeActionsEnabled={hasFluxPayExtension}
          pendingChargePayments={
            selectedSituation === "closed"
              ? undefined
              : billingData.activePaymentsEnriched
          }
          chargePaymentSlots={
            selectedSituation === "closed" ? undefined : billingData.pendingSlots
          }
          onBatchSendCharges={
            selectedSituation === "closed" ? undefined : openBatchSendDialog
          }
          loadingBatchCharge={loading}
          onAppointmentsChanged={() => {
            billingData.fetchBookedList();
            billingData.fetchPendingList();
          }}
        />
      </SituationDetailCard>
    ) : null;

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.25,
        }}
      >
        {segments.map((segment) => (
          <KpiCard
            key={segment.key}
            segment={segment}
            isSelected={segment.key === selectedSituation}
            onClick={
              segment.value > 0
                ? () => setSelectedSituation(segment.key)
                : undefined
            }
          />
        ))}
      </Box>

      {detailContent}

      <ConfirmBillingSendDialog
        isOpen={batchDialogOpen}
        onClose={closeBatchDialog}
        mode={batchSendMode}
        onConfirmSendAll={async () => {
          await sendCharges(pendingBatchPayments);
          closeBatchDialog();
        }}
        onConfirmSendMissing={async () => {
          await sendCharges(pendingUnsentBatchPayments);
          closeBatchDialog();
        }}
        onConfirmResendAll={async () => {
          await sendCharges(pendingBatchPayments);
          closeBatchDialog();
        }}
        loading={loading}
        error={error}
        groupInfo={{
          displayName: `${pendingBatchPayments.length} cobrança(s)`,
          total: sumPaymentDetailsAmount(pendingBatchPayments),
          unsentTotal: sumPaymentDetailsAmount(pendingUnsentBatchPayments),
          paymentsCount: pendingBatchPayments.length,
          unsentCount: pendingUnsentBatchPayments.length,
          sentCount:
            pendingBatchPayments.length - pendingUnsentBatchPayments.length,
        }}
      />
    </Stack>
  );
}
